import { JEWEL_DEFINITIONS } from './jewels';

/**
 * **Nenhuma passiva estatística pode registar um decorador de projétil ou um
 * VFX por disparo.**
 *
 * ## O que aconteceu, e porque esta regra existe
 *
 * Duas joias passivas ganharam apresentação própria no tiro comum: a de dano
 * recebeu uma coroa de fogo e uma esteira, a de cadência um clarão dourado no
 * cano. Cada uma isolada parecia razoável.
 *
 * Uma nave equipa **seis** sockets. Seis passivas decoradas sobre a mesma bala
 * dão seis camadas simultâneas por disparo, e o combate deixa de ser legível
 * exatamente no momento em que mais precisa de o ser. O problema não é nenhuma
 * das camadas - é que a bala comum não tem sítio para seis.
 *
 * Por isso a decisão autoral: uma joia que só muda um NÚMERO não muda o que se
 * vê num tiro. Quem informa que ela está equipada é a interface, que tem espaço
 * para seis linhas e nenhum orçamento de quadro.
 *
 * ## O que continua permitido, e é importante que continue
 *
 * Um **proc confirmado** que produz uma entidade ou um evento DIFERENTE pode ter
 * arte própria - míssil, bomba atómica, tiro verde de cura. Não é a bala comum
 * decorada: é outra coisa a acontecer, e desenhá-la é descrever o mundo em vez
 * de anotar uma estatística.
 *
 * A fronteira é essa, e não "efeitos visuais são maus":
 *
 * | classe | o que é | apresentação |
 * | --- | --- | --- |
 * | **A** passiva numérica | dano, cadência, vida, redução, Coração | **nenhuma no tiro** |
 * | **B** proc com projétil próprio | míssil, bomba, cura | arte própria, no proc |
 * | **C** proc de impacto | corte, serra, roubo, reflexão | no impacto, quando acontece |
 * | **D** estado/debuff | veneno, sangramento, escudo | **uma por alvo**, não por pilha |
 *
 * ## Como esta constante é usada
 *
 * `jewel-presentation.test.ts` varre o renderer e recusa que qualquer módulo
 * leia o perfil de joias para decidir textura, sprite, emissor ou partícula.
 * Uma passiva que voltasse a decorar um tiro falha essa prova, e a mensagem
 * aponta para aqui.
 */
export const PASSIVE_JEWELS_HAVE_NO_SHOT_VFX =
  'Nenhuma passiva estatística pode registar um decorador de projétil ou um VFX por disparo. ' +
  'Uma nave equipa seis sockets, e seis decorações sobre a mesma bala tornam o combate ' +
  'ilegível. Procs confirmados que produzem uma ENTIDADE diferente - míssil, bomba, cura - ' +
  'podem ter arte própria, porque não são a bala comum decorada.';

/**
 * As joias que são puramente estatísticas, e que portanto não desenham nada.
 *
 * Derivada do CATÁLOGO e não de uma lista à mão: uma joia passa a contar como
 * passiva por declarar um coeficiente numérico, e uma lista manual concordaria
 * consigo mesma para sempre - incluindo no dia em que alguém ligasse a terceira.
 *
 * O Coração Primordial entra pela mesma porta: ele soma dano e não ocupa
 * socket, e nunca teve nem pode ganhar assinatura no projétil.
 */
export const PURELY_STATISTICAL_JEWEL_IDS: readonly string[] = Object.freeze(
  JEWEL_DEFINITIONS.filter(
    (jewel) =>
      jewel.effects.damagePerLevelSumBps !== undefined ||
      jewel.effects.attackSpeedPerLevelSumBps !== undefined ||
      /*
       * A Égide Esmeralda entra por aqui (gate V28G.Q), e é a linha que a
       * impede de escolher arte.
       *
       * Ela é da classe **A** da tabela acima - uma passiva numérica - e é a
       * mais pura das quatro: as outras três mudam um número que o jogador vê a
       * acontecer (um golpe maior, uma salva mais cedo, uma barra a subir), e
       * esta muda um número que ele vê a NÃO acontecer. Não há entidade nova,
       * não há evento novo, não há instante próprio e não há nada para desenhar.
       *
       * Um escudo à volta da nave, um brilho verde ao levar dano, um "BLOCK" ou
       * um segundo número seriam exatamente as seis camadas que esta fronteira
       * existe para recusar - e a prova do renderer varre a lista que esta linha
       * alimenta.
       */
      jewel.effects.damageReductionPerLevelSumBps !== undefined ||
      /*
       * O Passo Alado entra por aqui (gate V28G.T), e é a linha que o impede de
       * escolher arte.
       *
       * Ele é da classe **A** da tabela acima, e é o caso mais claro de todos:
       * não há entidade nova, não há evento novo, não há instante próprio e não
       * há nada a acontecer que já não acontecesse. A nave anda, como sempre
       * andou - anda mais.
       *
       * Uma trilha, um brilho, um rasto no motor, uma aura, um pulso, uma
       * textura nova ou um som seriam exatamente as seis camadas que esta
       * fronteira existe para recusar, e a prova do renderer varre a lista que
       * esta linha alimenta. Quem informa que a joia está equipada é a interface,
       * pela frase numérica que o catálogo deriva.
       */
      jewel.effects.moveSpeedPerLevelSumBps !== undefined ||
      /*
       * O Coração do Titã entra por aqui (gate V28G.V), e é a linha que o impede
       * de escolher arte.
       *
       * Ele é da classe **A** desde que foi ligado - a tabela no topo deste
       * ficheiro sempre listou "vida" - e estava fora da lista por omissão, não
       * por decisão. A auditoria das catorze apanhou-o como **P2‑2**: uma classe
       * A ausente é uma guarda que não cobre o que devia.
       *
       * Não há entidade nova, não há evento novo e não há instante próprio: a
       * barra sobe mais alto, e é tudo. Um brilho ao encher, um contorno mais
       * grosso, uma aura de resistência ou um número sobre o casco seriam
       * exatamente as seis camadas que esta fronteira existe para recusar.
       *
       * ## E a escala do casco no duelo NÃO é uma exceção a isto
       *
       * Um casco com mais vida é desenhado maior desde o gate V28G.V, e essa
       * escala não é um VFX desta joia: é uma consequência da VIDA MÁXIMA, lida
       * de `hullScaleBps(maxHealthMilli, SHIP_BASE_MAX_HEALTH_MILLI)`. Uma nave
       * que chegue à mesma vida por pontos alocados mede exatamente o mesmo, e
       * nenhum módulo do renderer pergunta que joia a lá pôs.
       *
       * A distinção importa porque é o que mantém esta afirmação verdadeira: o
       * renderer não lê o perfil de joias, e a prova varre-o para o provar. Se a
       * escala fosse desta joia, ele teria de a nomear - e teria de nomear a
       * seguinte.
       */
      jewel.effects.maxHealthPerLevelSumBps !== undefined ||
      jewel.effects.damageBps !== undefined,
  )
    .map((jewel) => jewel.definitionId)
    .sort(),
);
